"use client";

import Link from "next/link";
import { useActionState, useEffect, useState, Suspense } from "react";
import { useSearchParams } from "next/navigation";
import { authenticate, googleSignIn } from "@/actions/auth";
import { Logo } from "@/components/ui/Logo";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { FileCheck, Shield, Zap } from "lucide-react";
import { PasswordInput } from "@/components/ui/PasswordInput";

const AUTH_ERROR_MESSAGES: Record<string, string> = {
    "account-disabled": "Tu cuenta ha sido deshabilitada. Contacta al administrador.",
    "account-deleted": "Esta cuenta ha sido eliminada. Contacta al administrador si crees que es un error.",
    "OAuthSignin": "Error al iniciar con Google. Intenta de nuevo.",
    "OAuthCallback": "Error en la respuesta de Google. Intenta de nuevo.",
    "OAuthCreateAccount": "No se pudo crear la cuenta con Google.",
    "Callback": "Error de autenticación. Intenta de nuevo.",
};

function UrlErrorBanner() {
    const searchParams = useSearchParams();
    const urlError = searchParams.get("error");
    if (!urlError) return null;
    const msg = AUTH_ERROR_MESSAGES[urlError] ?? "Error al iniciar sesión. Intenta de nuevo.";
    return (
        <div className="flex items-center justify-center">
            <p className="text-sm text-red-600 font-medium bg-red-50 px-4 py-2 rounded-lg border border-red-200">
                {msg}
            </p>
        </div>
    );
}

export default function LoginPage() {
    const [errorMessage, dispatch] = useActionState(authenticate, undefined);
    const [googleBlockedEnvironment, setGoogleBlockedEnvironment] = useState(false);
    const [browserUrl, setBrowserUrl] = useState("/login");
    // Adónde volver después de entrar; el servidor lo valida (lib/auth/destino-tras-login.ts).
    const [callbackUrl, setCallbackUrl] = useState("");

    useEffect(() => {
        const ua = navigator.userAgent || "";
        const isStandalone =
            (window.navigator as Navigator & { standalone?: boolean }).standalone === true ||
            window.matchMedia("(display-mode: standalone)").matches;

        // Google OAuth suele fallar en PWAs instaladas y navegadores embebidos/in-app.
        const isEmbeddedBrowser =
            /\b(wv|WebView)\b/i.test(ua) ||
            /\bFBAN\b|\bFBAV\b|Instagram|Line\/|TikTok|LinkedInApp|Snapchat|GSA\//i.test(ua);

        setGoogleBlockedEnvironment(isStandalone || isEmbeddedBrowser);
        setBrowserUrl(window.location.href);
        setCallbackUrl(new URLSearchParams(window.location.search).get("callbackUrl") ?? "");
    }, []);

    return (
        <div className="min-h-screen grid lg:grid-cols-2 relative overflow-hidden bg-slate-50">
            <div className="flex items-center justify-center p-8 relative z-10">
                <div className="absolute inset-0 opacity-30">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "radial-gradient(#cbd5e1 1px, transparent 1px)",
                            backgroundSize: "20px 20px",
                        }}
                    />
                </div>

                <div className="relative z-10 w-full max-w-md space-y-8">
                    <div className="text-center">
                        <div className="flex justify-center mb-6">
                            <Logo size="md" className="scale-90 md:scale-100 origin-center" />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900">Bienvenido de nuevo</h1>
                        <p className="text-slate-500 mt-2">Ingresa tus credenciales para continuar</p>
                    </div>

                    <div className="bg-white border border-slate-200 shadow-xl rounded-2xl p-8 space-y-5">
                        {googleBlockedEnvironment ? (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 space-y-3">
                                <div className="flex items-start gap-2">
                                    <span className="text-lg shrink-0">⚠️</span>
                                    <div>
                                        <p className="font-bold">Google no permite iniciar sesión desde la app instalada.</p>
                                        <p className="mt-1 text-amber-800">
                                            Esto es una restricción de Google para apps PWA. Tenés dos opciones:
                                        </p>
                                    </div>
                                </div>
                                <div className="space-y-2 pl-1">
                                    <div className="flex items-start gap-2 bg-white/60 rounded-lg p-3 border border-amber-200">
                                        <span className="font-bold text-blue-900 shrink-0">1.</span>
                                        <div>
                                            <p className="font-semibold text-slate-800">Abrí en Chrome</p>
                                            <p className="text-xs text-slate-500 mt-0.5">Copiá la URL y pegala en Chrome, o tocá el botón:</p>
                                            <a
                                                href={browserUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-2 inline-flex h-9 items-center justify-center rounded-lg bg-blue-900 px-4 text-xs font-semibold text-white transition-colors hover:bg-blue-800"
                                            >
                                                Abrir en navegador
                                            </a>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-2 bg-white/60 rounded-lg p-3 border border-amber-200">
                                        <span className="font-bold text-blue-900 shrink-0">2.</span>
                                        <div>
                                            <p className="font-semibold text-slate-800">Iniciá sesión con email y contraseña</p>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                ¿Solo tenés cuenta Google y nunca pusiste contraseña?{" "}
                                                <Link href="/forgot-password" className="text-blue-900 font-bold underline">
                                                    Creá una aquí
                                                </Link>
                                                {" "}y luego ingresá desde abajo.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <form action={googleSignIn}>
                                <input type="hidden" name="callbackUrl" value={callbackUrl} />
                                <Button
                                    className="w-full bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 shadow-sm h-12 flex items-center justify-center gap-2"
                                    type="submit"
                                    variant="outline"
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                                        <path
                                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                            fill="#4285F4"
                                        />
                                        <path
                                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                            fill="#34A853"
                                        />
                                        <path
                                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                            fill="#FBBC05"
                                        />
                                        <path
                                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                            fill="#EA4335"
                                        />
                                    </svg>
                                    Continuar con Google
                                </Button>
                            </form>
                        )}

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-slate-200" />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-white px-2 text-slate-500">O con email</span>
                            </div>
                        </div>

                        <form action={dispatch} className="space-y-5">
                            <input type="hidden" name="callbackUrl" value={callbackUrl} />
                            <div className="flex flex-col gap-4">
                                <Input
                                    label="Email"
                                    type="email"
                                    name="email"
                                    placeholder="nombre@ejemplo.com"
                                    required
                                    className="bg-slate-50 border-slate-200 focus:border-blue-900 focus:ring-blue-900/20"
                                />

                                <div className="space-y-2">
                                    <PasswordInput
                                        label="Contraseña"
                                        name="password"
                                        placeholder="••••••••"
                                        required
                                        className="bg-slate-50 border-slate-200 focus:border-blue-900 focus:ring-blue-900/20"
                                    />
                                    <div className="flex justify-end">
                                        <Link
                                            href="/forgot-password"
                                            className="text-xs text-blue-900 hover:text-amber-600 transition-colors"
                                        >
                                            ¿Olvidaste tu contraseña?
                                        </Link>
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    className="w-full rounded-lg bg-blue-900 hover:bg-blue-800 text-white shadow-lg shadow-blue-900/30 border-0 h-12 text-base font-semibold transition-all hover:shadow-xl"
                                >
                                    Iniciar Sesión
                                </button>
                            </div>

                            <Suspense fallback={null}>
                                <UrlErrorBanner />
                            </Suspense>

                            {errorMessage && (
                                <div className="flex items-center justify-center">
                                    <p className="text-sm text-red-600 font-medium bg-red-50 px-4 py-2 rounded-lg border border-red-200">
                                        {errorMessage}
                                    </p>
                                </div>
                            )}
                        </form>
                    </div>

                    <div className="text-center text-sm text-slate-500">
                        ¿No tienes cuenta?{" "}
                        <Link href="/register" className="font-semibold text-blue-900 hover:text-amber-600 transition-colors">
                            Crear Cuenta
                        </Link>
                    </div>
                </div>
            </div>

            <div className="hidden lg:flex relative overflow-hidden bg-blue-900 items-center justify-center p-12">
                <div className="absolute inset-0">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-blue-800 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-600 rounded-full blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2" />
                </div>

                <div className="absolute inset-0 opacity-10">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
                            backgroundSize: "30px 30px",
                        }}
                    />
                </div>

                <div className="relative z-10 max-w-lg text-center space-y-12">
                    <div className="space-y-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 backdrop-blur-sm">
                            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                            <span className="text-white/80 text-sm font-medium">Sistema DTE Certificado</span>
                        </div>

                        <h2 className="text-4xl font-bold text-white leading-tight">
                            Facturación Electrónica
                            <br />
                            <span className="text-amber-400">El Salvador</span>
                        </h2>

                        <p className="text-lg text-blue-200 leading-relaxed max-w-md mx-auto">
                            Sistema completo de documentos tributarios electrónicos, certificado por el Ministerio de Hacienda.
                        </p>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-white/10 rounded-xl border border-white/10 backdrop-blur-sm">
                            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center mx-auto mb-3">
                                <FileCheck className="w-5 h-5 text-white" />
                            </div>
                            <p className="text-white font-semibold text-sm">11 Tipos DTE</p>
                            <p className="text-blue-300 text-xs mt-1">Completos</p>
                        </div>
                        <div className="p-4 bg-white/10 rounded-xl border border-white/10 backdrop-blur-sm">
                            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center mx-auto mb-3">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <p className="text-white font-semibold text-sm">Firma Digital</p>
                            <p className="text-blue-300 text-xs mt-1">Certificada</p>
                        </div>
                        <div className="p-4 bg-white/10 rounded-xl border border-white/10 backdrop-blur-sm">
                            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center mx-auto mb-3">
                                <Zap className="w-5 h-5 text-white" />
                            </div>
                            <p className="text-white font-semibold text-sm">Transmisión</p>
                            <p className="text-blue-300 text-xs mt-1">Instantánea</p>
                        </div>
                    </div>

                    <div className="flex items-center justify-center gap-6 pt-4">
                        <div className="text-center">
                            <p className="text-3xl font-bold text-white">2.0</p>
                            <p className="text-blue-300 text-sm">Normativa MH</p>
                        </div>
                        <div className="w-px h-12 bg-white/20" />
                        <div className="text-center">
                            <p className="text-3xl font-bold text-white">15 min</p>
                            <p className="text-blue-300 text-sm">Reintento automático</p>
                        </div>
                        <div className="w-px h-12 bg-white/20" />
                        <div className="text-center">
                            <p className="text-3xl font-bold text-white">En línea</p>
                            <p className="text-blue-300 text-sm">Sin instalar nada</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
